import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, FINANCE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const defaultColSpan = {
	md: 6
};
const BankInfoForm = NiceModal.create(({ id, modalName }) => {
	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { closeModal } = useModalRouter();
	const schema = yup.object().shape({
		bankName: yup.string().required(),
		countryId: yup.number().required(),
		BIC: yup.string().required(),
		bankCode: yup.string().required()
	});

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const fillForm = useCallback(async () => {
		const defaultValues = {
			bankName: null,
			country: null,
			BIC: null,
			bankCode: null
		};
		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axios.post(FINANCE.GET_BANK, {
				id
			});

			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}

			return {
				...defaultValues,
				...data
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		watch,
		setValue,
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const countryAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'nameEn', width: 1000 }
		],
		[]
	);

	const onSubmit = async (data) => {
		const URL = id ? FINANCE.UPDATE_BANK : FINANCE.ADD_BANK;
		try {
			const response = await axios.post(URL, {
				...data,
				id
			});
			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.BANKS));
				showSuccess();
				modal.remove();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal maxWidth="small" fullwidth className="modal-addBankModal">
			<ModalTitle>
				<Typography variant="p1">addBank</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="bankName" required>
						<StringInput name="bankName" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="country" required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.COUNTRIES}
							columns={countryAutocompleteColumns}
							selectionLabelAccessor={['nameEn']}
							value={watch('countryId')}
							setValue={(value) => {
								setValue('countryId', value?.ID ?? null, { shouldDirty: true, shouldValidate: true });
							}}
							name="countryId"
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="BIC" required>
						<StringInput name="BIC" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="bankCode" required>
						<StringInput name="bankCode" />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						modal.remove();
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="save"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default BankInfoForm;
